/*  iceberg-ui  */
import {
    Button,
    Form,
    FormFieldGroup,
    FormModalSubmitButton
} from '@auviknetworks/iceberg-ui';

/*  MUI  */
import { Typography } from '@mui/material';

/*  Shared  */
import { type FieldSchemaJson } from 'application/Shared/Components/Form/Field.type';
import { FieldFactory } from 'application/Shared/Components/Form/FieldFactory';
import { FormFactoryContextProvider } from 'application/Shared/Contexts/FormFactoryContext';
import { Helpers, Notify } from 'application/Shared/Helpers';
import { Modal, ModalBody, ModalFooter } from 'application/Shared/Modals';
import {
    Recipient,
    usePostScheduledReport
} from 'hooks/dataAccess/Reports/scheduledReports';
import { useReportParams } from 'hooks/useReportParams';

/*  React  */
import React, { useState } from 'react';
import { UseFormSetError } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { ReportDetails } from './ReportDetails';

interface ScheduledReportsData {
    name: string;
    recipients: string[];
    notes: string;
    period: string;
}

const formDefinition: FieldSchemaJson[] = [
    {
        control: {
            type: 'text'
        },
        label: 'Reports.form.name.label',
        name: 'name',
        optional: false,
        type: 'string'
    },
    {
        control: {
            creatable: true,
            helperText: 'Reports.form.recipients.helperText',
            isClearable: true,
            isMulti: true,
            options: {
                operation: {
                    action: 'getUsers',
                    serviceType: 'asm',
                    transform: 'data.map(x, {"label": x.name, "value": x.id})'
                },
                type: 'api'
            },
            type: 'select'
        },
        label: 'Reports.form.recipients.label',
        name: 'recipients',
        optional: false,
        type: 'string'
    },
    {
        control: {
            type: 'text-area'
        },
        label: 'Reports.form.notes.label',
        name: 'notes',
        type: 'string'
    },
    {
        control: {
            helperText: 'Reports.period.description.{{period}}',
            options: {
                type: 'static',
                values: [
                    { label: 'Reports.period.daily', value: 'daily' },
                    { label: 'Reports.period.weekly', value: 'weekly' },
                    { label: 'Reports.period.monthly', value: 'monthly' },
                    { label: 'Reports.period.quarterly', value: 'quarterly' },
                    {
                        label: 'Reports.period.customQuarter',
                        value: 'customQuarter'
                    }
                ]
            },
            type: 'select'
        },
        label: 'Reports.form.period.label',
        name: 'period'
    }
];

export const ScheduleReportButtonWithModal = ({
    disabled = false
}: {
    disabled?: boolean;
}): JSX.Element => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const { partnerId, reportType } = useParams();
    const parsedReportParams = useReportParams(reportType);

    const { postScheduleReport } = usePostScheduledReport();

    const handleFormSubmit = async (data: ScheduledReportsData) => {
        const recipients: Recipient[] = data.recipients.map((recipient) =>
            Helpers.isUUID(recipient)
                ? { type: 'user', value: recipient }
                : { type: 'email', value: recipient }
        );

        const payload = {
            name: data.name,
            notes: data.notes,
            params: { ...parsedReportParams, period: data.period },
            platformTenantId: parsedReportParams.platformTenantId || partnerId!,
            recipients
        };

        const success = await postScheduleReport(payload);

        if (success) {
            Notify.s(t('Reports.ScheduledReport.successMessage'));
            setIsOpen(false);
        } else {
            Notify.error(t('Reports.ScheduledReport.errorMessage'));
        }
    };

    return (
        <>
            <Button
                data-trackid="reports_scheduleReport_modal_open"
                data-testid="reportsScheduleReportModalOpenBtn"
                onClick={() => setIsOpen(true)}
                disabled={disabled}
                variant="secondary"
                size="small"
            >
                {t('Reports.save_and_schedule')}
            </Button>
            <ScheduleReportModal
                handleClose={() => setIsOpen(false)}
                handleSubmit={handleFormSubmit}
                isOpen={isOpen}
            />
        </>
    );
};

export const ScheduleReportModal = ({
    handleSubmit,
    handleClose,
    isOpen
}: {
    handleClose: () => void;
    handleSubmit: (
        values: ScheduledReportsData,
        setError: UseFormSetError<ScheduledReportsData>
    ) => void;
    isOpen: boolean;
}) => {
    const { t } = useTranslation();
    const { clientId, partnerId } = useParams();

    const validate = (values: ScheduledReportsData) => {
        // Validate all recipient emails or UUIDs
        const validRecipients = values.recipients.every(
            (recipient) =>
                Helpers.isUUID(recipient) || Helpers.isEmail(recipient)
        );

        if (!validRecipients) {
            return {
                errors: {
                    recipients: {
                        message: t('Reports.sendEmail.errorInvalidEmails'),
                        type: 'validation'
                    }
                },
                values
            };
        }

        return { errors: {}, values };
    };

    return (
        <Modal
            onClose={handleClose}
            open={isOpen}
            paperProps={{ minWidth: '600px' }}
            title={t('Reports.save_and_schedule')}
            titleProps={{ showClose: true }}
        >
            <Typography variant="body1" sx={{ margin: '24px 24px 0' }}>
                {t('Reports.save_and_schedule.body')}
            </Typography>
            <Form<ScheduledReportsData>
                onSubmit={handleSubmit}
                validate={validate}
            >
                <ModalBody>
                    <FormFieldGroup>
                        <FormFactoryContextProvider
                            clientId={clientId || partnerId!}
                        >
                            {formDefinition.map((field: FieldSchemaJson) => (
                                <FieldFactory key={field.name} schema={field} />
                            ))}
                        </FormFactoryContextProvider>
                    </FormFieldGroup>

                    <ReportDetails />
                </ModalBody>

                <ModalFooter>
                    <Button
                        onClick={handleClose}
                        variant="secondary"
                        type="button"
                    >
                        {t('Common.cancel')}
                    </Button>
                    <FormModalSubmitButton>
                        {t('Reports.save_and_schedule')}
                    </FormModalSubmitButton>
                </ModalFooter>
            </Form>
        </Modal>
    );
};
